<template>
    <div class="flex flex-col h-screen overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100">
        <div class="flex-grow"></div>
        <div class="w-full max-w-md px-4 py-12 mx-auto">

            <div class="mb-8 text-center">
                <img class="h-16 mx-auto mb-6" src="../assets/logo_black.png" alt="CARU" />
                <h1 class="mb-2 text-3xl font-bold text-gray-900">{{ $t('login.welcomeBack') }}</h1>
                <p class="text-gray-600">{{ $t('login.signInToAccount') }}</p>
            </div>

            <div class="p-8 space-y-6 bg-white shadow-xl rounded-2xl">
                <form v-if="loginMethod === 'code' && !challengeIssued" @submit.prevent="handleSignIn" class="space-y-5">
                    <div>
                        <label for="email" class="block mb-2 text-sm font-semibold text-gray-700">
                            {{ $t('login.emailAddress') }}
                        </label>
                        <input id="email" v-model="email" type="email" :placeholder="$t('login.enterEmail')" required class="w-full px-4 py-3 transition duration-200 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                    <button type="submit" :disabled="isLoading || !email" class="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                        <span v-if="isLoading" class="flex items-center justify-center">
                            <svg class="w-5 h-5 mr-3 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {{ $t('login.sendingCode') }}
                        </span>
                        <span v-else>{{ $t('login.sendVerificationCode') }}</span>
                    </button>
                    <div class="mt-4 text-center">
                        <button type="button" @click="loginMethod = 'password'; error = null" class="text-sm font-medium text-gray-400 transition duration-200 ">
                            {{ $t('login.loginWithPassword') }}
                        </button>
                    </div>
                </form>

                <form v-if="loginMethod === 'code' && challengeIssued" @submit.prevent="handleConfirm" class="space-y-5">
                    <div class="mb-4 text-center">
                        <div class="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full">
                            <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                            </svg>
                        </div>
                        <h2 class="mb-2 text-2xl font-bold text-gray-900">{{ $t('login.checkYourEmail') }}</h2>
                        <p class="text-gray-600">
                            {{ $t('login.sentCodeTo') }}<br />
                            <span class="font-semibold text-gray-900 break-words">{{ email }}</span>
                        </p>
                        <p class="mt-4 text-xs text-gray-500">
                            {{ $t('login.checkSpamFolder') }}
                        </p>
                    </div>

                    <div>
                        <label for="code" class="block mb-2 text-sm font-semibold text-gray-700">
                            {{ $t('login.verificationCode') }}
                        </label>
                        <input id="code" v-model="code" type="text" :placeholder="$t('login.enterCode')" maxlength="6" required class="w-full px-4 py-3 transition duration-200 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>

                    <button type="submit" :disabled="isLoading || code.length !== 6" class="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                        <span v-if="isLoading" class="flex items-center justify-center">
                            <svg class="w-5 h-5 mr-3 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {{ $t('login.verifying') }}
                        </span>
                        <span v-else>{{ $t('login.verifyAndSignIn') }}</span>
                    </button>

                    <button type="button" @click="handleResendCode" :disabled="isResending" class="w-full py-2 font-medium text-blue-600 transition duration-200 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        <span v-if="isResending" class="flex items-center justify-center">
                            <svg class="w-4 h-4 mr-2 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {{ $t('login.resending') }}
                        </span>
                        <span v-else>{{ $t('login.resendCode') }}</span>
                    </button>

                    <div class="mt-4">
                        <button type="button" @click="challengeIssued = false; code = ''; error = null" class="w-full py-2 font-medium text-blue-600 transition duration-200 hover:text-blue-700">
                            {{ $t('login.useDifferentEmail') }}
                        </button>
                    </div>
                </form>

                <form v-if="loginMethod === 'password'" @submit.prevent="handleSignInPassword" class="space-y-5">
                    <div>
                        <label for="email-password" class="block mb-2 text-sm font-semibold text-gray-700">
                            {{ $t('login.emailAddress') }}
                        </label>
                        <input id="email-password" v-model="email" type="email" :placeholder="$t('login.enterEmail')" required class="w-full px-4 py-3 transition duration-200 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                    <div>
                        <div class="flex items-center justify-between mb-2">
                            <label for="password" class="block text-sm font-semibold text-gray-700">
                                {{ $t('login.password') }}
                            </label>
                            <button type="button" @click="loginMethod = 'forgotPassword'; error = null" class="text-sm font-medium text-blue-600 transition duration-200 hover:text-blue-500">
                                {{ $t('login.forgotPassword') }}
                            </button>
                        </div>
                        <input id="password" v-model="password" type="password" :placeholder="$t('login.enterPassword')" required class="w-full px-4 py-3 transition duration-200 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                    <button type="submit" :disabled="isLoading || !email || !password" class="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                        <span v-if="isLoading" class="flex items-center justify-center">
                            <svg class="w-5 h-5 mr-3 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {{ $t('login.signingIn') }}
                        </span>
                        <span v-else>{{ $t('login.signIn') }}</span>
                    </button>
                    <div class="mt-4 text-center">
                        <button type="button" @click="loginMethod = 'code'; error = null" class="text-sm font-medium text-gray-400 transition duration-200 ">
                            {{ $t('login.loginWithCode') }}
                        </button>
                    </div>
                </form>

                <form v-if="loginMethod === 'forgotPassword'" @submit.prevent="handleForgotPassword" class="space-y-5">
                    <div class="mb-4 text-center">
                        <div class="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full">
                            <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>
                            </svg>
                        </div>
                        <h2 class="mb-2 text-2xl font-bold text-gray-900">{{ $t('login.resetPassword') }}</h2>
                        <p class="text-gray-600">
                            {{ $t('login.resetPasswordDescription') }}
                        </p>
                    </div>
                    <div>
                        <label for="email-forgot" class="block mb-2 text-sm font-semibold text-gray-700">
                            {{ $t('login.emailAddress') }}
                        </label>
                        <input id="email-forgot" v-model="email" type="email" :placeholder="$t('login.enterEmail')" required class="w-full px-4 py-3 transition duration-200 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                    <button type="submit" :disabled="isLoading || !email" class="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                        <span v-if="isLoading" class="flex items-center justify-center">
                            <svg class="w-5 h-5 mr-3 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {{ $t('login.sendingCode') }}
                        </span>
                        <span v-else>{{ $t('login.sendResetCode') }}</span>
                    </button>
                    <div class="mt-4 text-center">
                        <button type="button" @click="loginMethod = 'password'; error = null" class="text-sm font-medium text-gray-400 transition duration-200 hover:text-gray-600">
                            {{ $t('login.backToLogin') }}
                        </button>
                    </div>
                </form>

                <form v-if="loginMethod === 'resetPassword'" @submit.prevent="handleConfirmResetPassword" class="space-y-5">
                    <div class="mb-4 text-center">
                        <div class="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full">
                            <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                            </svg>
                        </div>
                        <h2 class="mb-2 text-2xl font-bold text-gray-900">{{ $t('login.checkYourEmail') }}</h2>
                        <p class="text-gray-600" v-if="email">
                            {{ $t('login.sentCodeTo') }}<br />
                            <span class="font-semibold text-gray-900 break-words">{{ email }}</span>
                        </p>
                        <p class="text-gray-600" v-else>
                            {{ $t('login.resetPasswordDescription') }}
                        </p>
                        <p class="mt-4 text-xs text-gray-500" v-if="email">
                            {{ $t('login.checkSpamFolder') }}
                        </p>
                    </div>
                    <div>
                        <label for="reset-email" class="block mb-2 text-sm font-semibold text-gray-700">
                            {{ $t('login.emailAddress') }}
                        </label>
                        <input id="reset-email" v-model="email" type="email" :placeholder="$t('login.enterEmail')" required class="w-full px-4 py-3 transition duration-200 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                    <div>
                        <label for="reset-code" class="block mb-2 text-sm font-semibold text-gray-700">
                            {{ $t('login.verificationCode') }}
                        </label>
                        <input id="reset-code" v-model="code" type="text" :placeholder="$t('login.enterCode')" maxlength="6" required class="w-full px-4 py-3 transition duration-200 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                    <div>
                        <label for="new-password" class="block mb-2 text-sm font-semibold text-gray-700">
                            {{ $t('login.newPassword') }}
                        </label>
                        <input id="new-password" v-model="newPassword" type="password" :placeholder="$t('login.enterNewPassword')" required class="w-full px-4 py-3 transition duration-200 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                    <button type="submit" :disabled="isLoading || code.length !== 6 || !newPassword" class="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                        <span v-if="isLoading" class="flex items-center justify-center">
                            <svg class="w-5 h-5 mr-3 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {{ $t('login.verifying') }}
                        </span>
                        <span v-else>{{ $t('login.resetPassword') }}</span>
                    </button>
                    <div class="mt-4 text-center">
                        <button type="button" @click="loginMethod = 'password'; error = null; code = ''; newPassword = ''" class="text-sm font-medium text-gray-400 transition duration-200 hover:text-gray-600">
                            {{ $t('login.backToLogin') }}
                        </button>
                    </div>
                </form>

                <div v-if="error" class="p-4 border border-red-200 rounded-lg bg-red-50">
                    <div class="flex items-start">
                        <svg class="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                        </svg>
                        <p class="text-sm text-red-800">{{ error }}</p>
                    </div>
                </div>
            </div>

            <p class="mt-6 text-sm text-center text-gray-600">
                © {{ new Date().getFullYear() }} CARU AG. {{ $t('login.allRightsReserved') }}
            </p>
        </div>
        <div class="flex-grow"></div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { signIn, confirmSignIn, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useStore } from 'vuex';

const { t } = useI18n();

const store = useStore();
const email = ref('');
const password = ref('');
const newPassword = ref('');
const loginMethod = ref('code');
const code = ref('');
const isLoading = ref(false);
const isResending = ref(false);
const challengeIssued = ref(false);
const error = ref(null);
const router = useRouter();
const route = useRoute();

onMounted(() => {
    const redirectParam = route.query.redirect || '';
    if (route.query.action === 'resetPassword' || redirectParam.includes('action=resetPassword')) {
        loginMethod.value = 'resetPassword';
    }
});

const logAuthenticationError = (action, err) => {
    const errorName = err?.name || err?.code || 'UnknownError';
    const errorMessage = err?.message || err?.toString?.() || 'Unknown error';

    console.error(`[LoginView] ${action} failed: ${errorName} - ${errorMessage}`, err);
};

const getErrorMessage = (err) => {
    const errorMessage = err.message || err.toString();
    const errorCode = err.name || err.code;

    const errorMap = {
        'UserNotFoundException': 'login.errors.userNotFound',
        'NotAuthorizedException': 'login.errors.invalidCode',
        'User does not exist': 'login.errors.userNotFound',
        'User is disabled': 'login.errors.userDisabled',
        'User is not confirmed': 'login.errors.userNotConfirmed',
        'LimitExceededException': 'login.errors.tooManyAttempts',
        'TooManyRequestsException': 'login.errors.tooManyAttempts',
        'TooManyFailedAttemptsException': 'login.errors.accountLocked',
        'Invalid session for the user': 'login.errors.sessionInvalid',
        'Too many failed attempts': 'login.errors.tooManyAttempts',
        'Code mismatch': 'login.errors.invalidCode',
        'Invalid verification code provided': 'login.errors.invalidCode',
        'User does not have an email': 'login.errors.noEmailAttribute',
        'NetworkError': 'login.errors.networkError',
    };

    if (errorCode && errorMap[errorCode]) {
        return t(errorMap[errorCode]);
    }

    for (const [key, translationKey] of Object.entries(errorMap)) {
        if (errorMessage.includes(key)) {
            return t(translationKey);
        }
    }

    if (errorMessage.toLowerCase().includes('expired')) {
        return t('login.errors.otpExpired');
    }

    return t('login.errors.generic');
};

const handleSignIn = async () => {
    isLoading.value = true;
    error.value = null;
    try {
        const result = await signIn({
            username: email.value,
            options: {
                authFlowType: 'CUSTOM_WITHOUT_SRP'
            }
        });

        if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
            challengeIssued.value = true;
        } else {
            error.value = `${t('login.errors.unexpectedStep')}: ${result.nextStep.signInStep}`;
        }

    } catch (err) {
        logAuthenticationError('Code sign-in', err);
        error.value = getErrorMessage(err);
    }
    isLoading.value = false;
};

const handleSignInPassword = async () => {
    isLoading.value = true;
    error.value = null;
    try {
        const result = await signIn({
            username: email.value,
            password: password.value,
        });

        if (result.isSignedIn) {
            router.push({ name: "devices", replace: true });
        } else if (result.nextStep.signInStep === 'NEW_PASSWORD_REQUIRED') {
            error.value = 'New password required, please contact support or use a different login method.';
        } else {
            error.value = `${t('login.errors.unexpectedStep')}: ${result.nextStep.signInStep}`;
        }
    } catch (err) {
        logAuthenticationError('Password sign-in', err);
        error.value = getErrorMessage(err);
    }
    isLoading.value = false;
};

const handleForgotPassword = async () => {
    isLoading.value = true;
    error.value = null;
    try {
        await resetPassword({ username: email.value });
        loginMethod.value = 'resetPassword';
    } catch (err) {
        logAuthenticationError('Password reset request', err);
        error.value = getErrorMessage(err);
    }
    isLoading.value = false;
};

const handleConfirmResetPassword = async () => {
    isLoading.value = true;
    error.value = null;
    try {
        await confirmResetPassword({
            username: email.value,
            confirmationCode: code.value,
            newPassword: newPassword.value
        });
        loginMethod.value = 'password';
        await store.dispatch('showNotification', {
            title: t('login.passwordResetSuccess'),
            text: '',
            type: 'info'
        });
    } catch (err) {
        logAuthenticationError('Password reset confirmation', err);
        error.value = getErrorMessage(err);
    }
    isLoading.value = false;
};

const handleResendCode = async () => {
    isResending.value = true;
    error.value = null;
    code.value = '';
    try {
        const result = await signIn({
            username: email.value,
            options: {
                authFlowType: 'CUSTOM_WITHOUT_SRP'
            }
        });

        if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
            await store.dispatch('showNotification', {
                title: t('login.codeResent'),
                text: t('login.codeResentMessage'),
                type: 'info'
            });
        } else {
            error.value = `${t('login.errors.unexpectedStep')}: ${result.nextStep.signInStep}`;
        }

    } catch (err) {
        logAuthenticationError('Verification code resend', err);
        error.value = getErrorMessage(err);
    }
    isResending.value = false;
};

const handleConfirm = async () => {
    isLoading.value = true;
    error.value = null;
    try {
        const result = await confirmSignIn({
            challengeResponse: code.value,
        });

        if (result.isSignedIn) {
            router.push({ name: "devices", replace: true });
        } else {
            error.value = t('login.errors.signInNotCompleted');
        }
    } catch (err) {
        logAuthenticationError('Verification code confirmation', err);
        error.value = getErrorMessage(err);
    }
    isLoading.value = false;
};
</script>

<style scoped>
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

form {
    animation: fadeIn 0.3s ease-out;
}
</style>
